import type { TAttachmentType } from '@doutorsim/shared/exposed/application-attachments'
import type { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Download, EllipsisVertical, Loader2, Trash2 } from 'lucide-react'
import { useState } from 'react'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/_components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/_components/ui/popover'
import { Text } from '@/features/_components/ui/typography'
import { FILE_TYPE_LABELS } from '@/features/_constants/person-receipt'

dayjs.extend(relativeTime)
dayjs.locale('pt-br')

export type TAttachmentColumns = {
  id: string
  filename: string
  type: TAttachmentType
  sizeBytes: number
  createdAt: Date
  userType: 'staff' | 'tenant'
  onView: (id: string) => void
  onDownload: (id: string) => void
  loadingId: string | null
  onDelete: (id: string, onSuccess: () => void) => void
  isDeleting: boolean
  deletingId: string | null
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)}KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`
}

function StaffActions({ row }: { row: TAttachmentColumns }) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { id, onDownload, loadingId, onDelete, isDeleting, deletingId } = row
  const isLoading = loadingId === id
  const isCurrentDeleting = isDeleting && deletingId === id

  return (
    <div className="flex items-center justify-center px-4 py-3">
      <Popover>
        <PopoverTrigger asChild>
          <button className="text-brand-grayscale-7 hover:text-brand-grayscale-10">
            <EllipsisVertical size={18} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-48 p-1">
          <button
            onClick={() => onDownload(id)}
            disabled={isLoading}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-brand-grayscale-2 disabled:opacity-50"
          >
            <Download size={16} />
            {isLoading ? 'Carregando...' : 'Baixar'}
          </button>

          <button
            disabled={isCurrentDeleting}
            onClick={() => setIsDeleteDialogOpen(true)}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-aux-red-danger-6 hover:bg-brand-grayscale-2 disabled:opacity-50"
          >
            {isCurrentDeleting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
            Remover
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="flex w-[389px] flex-col p-5">
          <DialogTitle>Deseja remover o documento?</DialogTitle>
          <Text>Este documento será deletado ao prosseguir.</Text>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="gray" className="font-bold" disabled={isCurrentDeleting}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              onClick={() => onDelete(id, () => setIsDeleteDialogOpen(false))}
              disabled={isCurrentDeleting}
            >
              {isCurrentDeleting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const attachmentColumns: ColumnDef<TAttachmentColumns>[] = [
  {
    accessorKey: 'filename',
    header: 'Arquivo',
    cell: ({ row }) => {
      const { id, filename, onView, loadingId } = row.original
      const isLoading = loadingId === id

      return (
        <button
          onClick={() => onView(id)}
          disabled={isLoading}
          className="inline-flex w-full px-8 py-3 text-left hover:underline disabled:opacity-50"
        >
          <span className="truncate">{isLoading ? 'Carregando...' : filename}</span>
        </button>
      )
    },
  },
  {
    accessorKey: 'type',
    header: 'Tipo',
    cell: ({ row }) => (
      <div className="px-8 py-3">
        <span>{FILE_TYPE_LABELS[row.original.type] ?? row.original.type}</span>
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Adicionado',
    meta: { width: '200px' },
    cell: ({ row }) => (
      <div className="px-8 py-3">
        <span title={dayjs(row.original.createdAt).format('DD/MM/YYYY HH:mm')}>
          {dayjs(row.original.createdAt).fromNow()}
        </span>
      </div>
    ),
  },
  {
    accessorKey: 'sizeBytes',
    header: 'Tamanho',
    meta: { width: '120px' },
    cell: ({ row }) => (
      <div className="px-8 py-3">
        <span>{formatSize(row.original.sizeBytes)}</span>
      </div>
    ),
  },
  {
    id: 'actions',
    header: '',
    meta: { width: '80px' },
    cell: ({ row }) => {
      const { id, userType, onDownload, loadingId } = row.original
      const isLoading = loadingId === id

      if (userType === 'tenant') {
        return (
          <div className="flex items-center justify-center px-4 py-3">
            <button
              onClick={() => onDownload(id)}
              disabled={isLoading}
              className="text-brand-grayscale-7 hover:text-brand-grayscale-10 disabled:opacity-50"
            >
              <Download size={18} />
            </button>
          </div>
        )
      }

      return <StaffActions row={row.original} />
    },
  },
]
